import { useAtomValue } from 'jotai';
import { isMobileAtom } from '@/jotai/atoms/userAtom';
import {
  Button,
  Col,
  Form,
  Input,
  Row,
  TableColumnType,
  message,
  Tabs,
  Table,
} from 'antd';

import './index.css';
import { useQuery } from '@tanstack/react-query';
import {
  getPictureFrameList,
  PictureFrame,
  PictureFrameExamineStatus,
  PictureFrameListParams,
} from '@/api/pictureFrame';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const PictureFramePage = () => {
  const isMobile = useAtomValue(isMobileAtom);
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();

  const [form] = Form.useForm<{
    userId: string;
    frameId: string;
    frameName: string;
  }>();

  const [tabKey, setTabKey] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = isMobile ? 10 : 20;

  const {
    data,
    isLoading,
    refetch: refetchPictureFrameList,
  } = useQuery({
    queryKey: ['picture-frame-list', tabKey, currentPage],
    queryFn: async () => {
      const values = form.getFieldsValue();
      const params: PictureFrameListParams = {
        currentPage,
        pageSize,
        userId: values.userId ? Number(values.userId) : undefined,
        id: values.frameId ? Number(values.frameId) : undefined,
        frameName: values.frameName || undefined,
        examineStatus: tabKey === 'all' ? undefined : Number(tabKey),
      };
      const resp = await getPictureFrameList(params);
      if (resp.status !== 200) {
        messageApi.error(resp.message);
        return {
          total: 0,
          records: [],
        };
      }
      return resp.data;
    },
    refetchOnWindowFocus: false,
  });

  const handleSearch = () => {
    setCurrentPage(1);
    void refetchPictureFrameList();
  };

  const handleTabChange = (key: string) => {
    setTabKey(key);
    setCurrentPage(1);
  };

  const tabItems = useMemo(() => {
    return [
      {
        label: '待审核',
        key: PictureFrameExamineStatus.PENDING.toString(),
      },
      {
        label: '通过',
        key: PictureFrameExamineStatus.APPROVED.toString(),
      },
      {
        label: '不通过',
        key: PictureFrameExamineStatus.REJECTED.toString(),
      },
      {
        label: '失效',
        key: PictureFrameExamineStatus.EXPIRED.toString(),
      },
      {
        label: '全部',
        key: 'all',
      },
    ];
  }, []);

  const columns: TableColumnType<PictureFrame>[] = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: '画框名称',
      dataIndex: 'frameName',
      key: 'frameName',
    },
    {
      title: '售价',
      dataIndex: 'price',
      key: 'price',
    },
    {
      title: '类型',
      dataIndex: 'publicationType',
      key: 'publicationType',
      render: (_, record) => {
        return record.publicationType === 0 ? '首次发布' : '编辑发布';
      },
    },
    {
      title: '提交时间',
      dataIndex: 'createTime',
      key: 'createTime',
    },
    {
      title: 'SUPP ID',
      dataIndex: 'suppId',
      key: 'suppId',
    },
    {
      title: '用户名',
      dataIndex: 'userName',
      key: 'userName',
    },
    {
      title: '状态',
      dataIndex: 'examineStatus',
      key: 'examineStatus',
      render: (_, record) => {
        switch (record.examineStatus) {
          case PictureFrameExamineStatus.PENDING:
            return '待审核';
          case PictureFrameExamineStatus.APPROVED:
            return '通过';
          case PictureFrameExamineStatus.REJECTED:
            return '不通过';
          case PictureFrameExamineStatus.EXPIRED:
            return '失效';
        }
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_, __, index: number) => {
        return (
          <Button type="link" onClick={() => handleView(index)}>
            查看
          </Button>
        );
      },
    },
  ];

  const handleView = (index: number) => {
    const ids = data?.records.map((item) => item.id);
    if (!ids) {
      return;
    }
    const searchParams = new URLSearchParams();
    searchParams.set('index', index.toString());
    searchParams.set('ids', ids.join(','));
    navigate(`/picture-frame/detail?${searchParams.toString()}`);
  };

  return (
    <div className="picture-frame-container">
      {contextHolder}
      <Form
        form={form}
        labelCol={{
          span: 8,
        }}
        wrapperCol={{
          span: 16,
        }}
        labelAlign="left"
        onFinish={handleSearch}
      >
        <Row gutter={24}>
          <Col span={12} xs={24} sm={12} md={8}>
            <Form.Item name="userId" label="SUPP ID">
              <Input size="small" />
            </Form.Item>
          </Col>
          <Col span={12} xs={24} sm={12} md={8}>
            <Form.Item name="frameId" label="画框ID">
              <Input size="small" />
            </Form.Item>
          </Col>
          <Col span={12} xs={24} sm={12} md={8}>
            <Form.Item name="frameName" label="画框名称">
              <Input size="small" />
            </Form.Item>
          </Col>
        </Row>
        <div>
          <Button type="primary" htmlType="submit">
            搜索
          </Button>
        </div>
      </Form>
      <div className="table-container">
        <Tabs
          type="card"
          size="small"
          items={tabItems}
          activeKey={tabKey}
          onChange={handleTabChange}
          className="tabs-container"
        />
      </div>
      <Table
        size="small"
        loading={isLoading}
        columns={columns}
        dataSource={data?.records}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        pagination={{
          size: isMobile ? 'small' : 'default',
          pageSize,
          showSizeChanger: false,
          total: data?.total,
          current: currentPage,
          onChange: (page) => setCurrentPage(page),
        }}
      />
    </div>
  );
};

export default PictureFramePage;
